import {
  type LangCode,
  type MeetingSummary,
  type SimulationScenario,
  langMeta,
} from "@scoach/types";

export interface SimulationContext {
  clientName: string;
  contactName?: string;
  contactRole?: string;
  industry?: string;
  stage: string;
  meetingGoal?: string;
  summary?: MeetingSummary | null;
  scenario?: SimulationScenario | null;
  companyName?: string;
  /** Language the AI client must speak in. Defaults to English when unset. */
  language?: LangCode;
}

export function buildSimulationPrompt(ctx: SimulationContext): string {
  const parts: string[] = [];

  if (ctx.language && ctx.language !== "en") {
    const langName = langMeta(ctx.language).labelEn;
    parts.push(
      `CRITICAL LANGUAGE RULE: You MUST speak ONLY in ${langName}. Every single sentence — your greeting, your questions, your reactions — must be in ${langName}. Do NOT use English or any other language, even if the rep speaks English. Stay in ${langName} for the entire conversation.`,
      "",
    );
  }

  if (ctx.scenario) {
    const s = ctx.scenario;
    parts.push(
      `You are ${s.persona}, ${s.personaRole} at ${s.company} (${s.industry}).`,
      s.scenarioDescription,
      "",
      "YOUR PERSONALITY:",
      ...s.personalityTraits.map((t) => `- ${t}`),
      "",
      "HIDDEN OBJECTIONS (do NOT reveal these up front — only bring them up when the conversation naturally leads there, or when the rep fails to address your concerns):",
      ...s.hiddenObjections.map((o) => `- ${o}`),
      "",
      s.systemPromptOverlay,
    );
  } else {
    const contactDesc = ctx.contactName
      ? `${ctx.contactName}${ctx.contactRole ? `, ${ctx.contactRole}` : ""}`
      : "a senior decision-maker";
    const target = ctx.companyName ? `${ctx.companyName}'s offerings` : "the vendor's solutions";
    parts.push(
      `You are ${contactDesc} at ${ctx.clientName}${ctx.industry ? ` (${ctx.industry})` : ""}.`,
      `You are a potential client evaluating ${target} for your organization.`,
    );
  }

  parts.push(
    "",
    "CORE RULES:",
    "- Stay in character at all times. You are a real person — NEVER acknowledge you are AI.",
    "- Speak naturally in short sentences (1-3 per turn), like a real business phone call.",
    "- BE PROACTIVE: Don't just answer questions — ask your own. Drive the conversation forward.",
    "- Ask tough, specific questions: pricing details, competitor comparisons, implementation risks, SLA guarantees, support quality.",
    "- Challenge vague or marketing-speak answers. Push for specifics: 'Can you give me a concrete example?' or 'What does that actually cost?'",
    "- Bring up competitors or alternative approaches naturally: 'We've been looking at market alternatives and they offer X — how do you compare?'",
    "- If the rep makes a good point, acknowledge it briefly but follow up with a harder question.",
    "- Raise budget concerns, timeline concerns, or team readiness concerns when appropriate.",
    "- Don't agree too easily. Real clients need convincing.",
    `- The meeting stage is: ${ctx.stage}.`,
  );

  if (ctx.summary) {
    const s = ctx.summary.internal;
    if (s.wentWell?.length) {
      parts.push("", "PREVIOUS MEETING — what went well (reference these positively):");
      for (const item of s.wentWell.slice(0, 3)) parts.push(`- ${item}`);
    }
    if (s.couldImprove?.length) {
      parts.push("", "PREVIOUS MEETING — weak points to probe (push the rep on these):");
      for (const item of s.couldImprove.slice(0, 3)) parts.push(`- ${item}`);
    }
    if (s.risks?.length) {
      parts.push("", "YOUR ACTIVE CONCERNS (raise these early):");
      for (const item of s.risks.slice(0, 3)) parts.push(`- ${item}`);
    }
    const openItems = s.actionItems?.filter((a) => !a.done) ?? [];
    if (openItems.length > 0) {
      parts.push("", "OUTSTANDING ACTION ITEMS (demand updates on these):");
      for (const item of openItems.slice(0, 5)) {
        parts.push(`- ${item.what} (assigned to: ${item.who || "unassigned"})`);
      }
    }
    const allNeeds = [...(s.needs?.stated ?? []), ...(s.needs?.actual ?? [])];
    if (allNeeds.length) {
      parts.push("", "YOUR BUSINESS NEEDS:");
      for (const item of allNeeds.slice(0, 3)) parts.push(`- ${item}`);
    }
  }

  if (ctx.meetingGoal) {
    parts.push("", `THE REP'S GOAL: ${ctx.meetingGoal}`);
    parts.push("Don't make it easy for them. Challenge their approach and push for specifics.");
  }

  parts.push(
    "",
    "Start by greeting the rep briefly and then immediately ask a pointed question about what they want to discuss today and why you should prioritize this meeting.",
  );

  return parts.join("\n");
}
